// ─── Track Definitions ───────────────────────────────────────────────────────
export const TRACKS = {
  A: {
    id: 'A',
    name: 'The Recruit',
    description: 'New to AI — starting from zero',
    startModule: 'module-0',
    startLesson: 'lesson-0',
  },
  B: {
    id: 'B',
    name: 'The Operator',
    description: 'Using AI but getting inconsistent results',
    startModule: 'module-0',
    startLesson: 'lesson-1',
  },
  C: {
    id: 'C',
    name: 'The Specialist',
    description: 'Months of daily use — hitting a ceiling',
    startModule: 'module-1',
    startLesson: 'lesson-0',
  },
};

// ─── Tier Access ─────────────────────────────────────────────────────────────
// Maps subscription tier → array of module IDs the user can access.
export const TIER_ACCESS = {
  foundations: ['module-0', 'module-1'],
  operator:    ['module-0', 'module-1', 'module-2', 'module-3', 'module-4'],
  certified:   ['module-0', 'module-1', 'module-2', 'module-3', 'module-4'],
  // Beta testers get full access without payment
  beta:        ['module-0', 'module-1', 'module-2', 'module-3', 'module-4'],
};

// ─── Placement Questions ──────────────────────────────────────────────────────
// Scenario-based concept tests. Each question is grounded in a specific module's
// core principle. The correct answer requires understanding the actual mechanic —
// not pattern-matching to the "expert sounding" option. All four choices are
// written to be plausible; only someone who knows the material picks the right one.
export const PLACEMENT_QUESTIONS = [
  {
    id: 'q1',
    module: 'module-0',
    concept: 'Tool Selection',
    question: 'You need to manage a complex 40-page research project in AI: analyze the entire document, cross-reference themes across dozens of sections, and produce a report that stays consistent throughout. Which approach gives you the best outcome?',
    options: [
      { label: 'Use standard ChatGPT — it handles long documents well', score: { A: 2 } },
      { label: 'Use a reasoning or long-context model built for tasks that require holding intent across many steps', score: { C: 3 } },
      { label: 'Break it into chunks and paste each section separately, asking one question at a time', score: { B: 2 } },
      { label: 'Any model works for this — it comes down to how well you write your prompts', score: { A: 3 } },
    ],
  },
  {
    id: 'q2',
    module: 'module-0',
    concept: 'Token Economy',
    question: 'You write a vague 20-word prompt. The AI gives a wrong answer. You correct it twice before getting something usable — four exchanges total. A colleague writes a detailed 80-word brief and nails it on the first response. Who spent more total tokens?',
    options: [
      { label: 'You did — every back-and-forth exchange costs tokens on both sides, and corrections compound the spend', score: { C: 3 } },
      { label: 'Your colleague did — longer prompts cost more upfront', score: { A: 3 } },
      { label: 'About equal — your corrections were short so it likely evened out', score: { B: 2 } },
      { label: 'It depends on the platform — some charge per session, not per token', score: { A: 2 } },
    ],
  },
  {
    id: 'q3',
    module: 'module-1',
    concept: 'Context Loading',
    question: 'A teammate shows you their prompt: "Write a marketing email." Before they hit send, what is the single most important problem to address?',
    options: [
      { label: 'They should specify their budget — cost context affects what AI can produce', score: { A: 3 } },
      { label: 'They should change it to "write a great marketing email" for better output', score: { A: 2 } },
      { label: "They haven't loaded any context: who receives it, what product it's for, what the goal is, or what tone to use", score: { C: 3 } },
      { label: "It's a fine starting point — they can always request revisions after seeing the first draft", score: { B: 2 } },
    ],
  },
  {
    id: 'q4',
    module: 'module-1',
    concept: 'Pre-Session Clarity',
    question: 'Before opening any AI session, you should be able to answer three questions clearly. Which set is correct?',
    options: [
      { label: 'What tone do I want, how long should it be, and which AI platform am I using', score: { A: 2 } },
      { label: 'What specific deliverable do I need, who is the audience and what do they need, and what constraints apply', score: { C: 3 } },
      { label: 'What topic am I working on, what have I tried before, and do I have time to review the result', score: { B: 1, A: 2 } },
      { label: "Is this creative or analytical, what's my deadline, and am I using GPT or Claude", score: { B: 2 } },
    ],
  },
  {
    id: 'q5',
    module: 'module-2',
    concept: 'Directive Precision',
    question: "The AI gives you a response where the structure is good but the tone is completely wrong. You type: \"That's not quite right — try again.\" What is the most likely outcome?",
    options: [
      { label: 'The model makes a targeted improvement based on what was clearly off', score: { B: 2 } },
      { label: 'The model asks you what specifically needs to change', score: { A: 2 } },
      { label: 'The model guesses what to fix — and likely adjusts something you didn\'t mean while missing what you actually needed', score: { C: 3 } },
      { label: 'The session is spent from accumulated context rot — better to start a new one', score: { A: 2 } },
    ],
  },
  {
    id: 'q6',
    module: 'module-2',
    concept: 'Hallucination & Verification',
    question: 'The AI delivers a confident, well-formatted response citing five specific statistics by name and source. You have no way to verify them right now. What does an Operator do?',
    options: [
      { label: "Trust them — the model was trained on massive data and rarely gets specifics wrong", score: { A: 3 } },
      { label: 'Accept them and add an "AI-generated" disclaimer to the document as a hedge', score: { B: 1 } },
      { label: 'Treat every fact as unverified until independently confirmed — AI confidence does not correlate with accuracy', score: { C: 3 } },
      { label: "Ask the model if it's certain — it will flag anything it isn't sure about", score: { A: 3 } },
    ],
  },
  {
    id: 'q7',
    module: 'module-3',
    concept: 'Prompt Chaining',
    question: 'You have a 6-step content workflow: research → outline → draft → review → revise → format. Which approach produces the most reliable and steerable results?',
    options: [
      { label: 'One prompt with all six instructions — fewer sessions means better continuity', score: { A: 2 } },
      { label: 'Six separate prompts, each feeding the next, reviewing and steering the output at each stage before moving forward', score: { C: 3 } },
      { label: 'Three prompts — group related steps to reduce back-and-forth', score: { B: 2 } },
      { label: "Let the model decide the sequence — it often finds a more efficient order on its own", score: { A: 3 } },
    ],
  },
];

// ─── Module & Lesson Structure ────────────────────────────────────────────────
export const MODULES = [
  {
    id: 'module-0',
    order: 0,
    title: 'The Crew',
    subtitle: 'Orientation — What You\'re Actually Working With',
    gated: false,
    lessons: [
      { id: 'lesson-0', order: 0, title: 'Meet Your Crew', duration: '8 min', tracks: ['A'] },
      { id: 'lesson-1', order: 1, title: 'Economy of Attention', duration: '10 min', tracks: ['A', 'B'] },
      { id: 'lesson-2', order: 2, title: 'Plan Before You Type', duration: '12 min', tracks: ['A', 'B'] },
      { id: 'lesson-3', order: 3, title: 'Know When to Stop', duration: '10 min', tracks: ['A', 'B'] },
    ],
  },
  {
    id: 'module-1',
    order: 1,
    title: 'The Brief',
    subtitle: 'Why Context Changes Everything',
    gated: true,
    passingScore: 75,
    lessons: [
      { id: 'lesson-0', order: 0, title: 'Why Context Is the Whole Job', duration: '12 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-1', order: 1, title: 'The Five-Part Brief', duration: '15 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-2', order: 2, title: 'Background Loading', duration: '10 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-3', order: 3, title: 'Clear Objective', duration: '12 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-4', order: 4, title: 'The Blindfold Drill', duration: '20 min', tracks: ['A', 'B', 'C'] },
    ],
  },
  {
    id: 'module-2',
    order: 2,
    title: 'The Directive',
    subtitle: 'Steering the Machine',
    gated: true,
    passingScore: 75,
    lessons: [
      { id: 'lesson-0', order: 0, title: 'Read Critically', duration: '10 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-1', order: 1, title: 'Steering Vocabulary', duration: '12 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-2', order: 2, title: 'Keeping Your Thread', duration: '10 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-3', order: 3, title: 'Confidently Wrong', duration: '12 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-4', order: 4, title: 'Restart Clean', duration: '10 min', tracks: ['A', 'B', 'C'] },
    ],
  },
  {
    id: 'module-3',
    order: 3,
    title: 'The Stack',
    subtitle: 'Building Your Workflow',
    gated: true,
    passingScore: 75,
    lessons: [
      { id: 'lesson-0', order: 0, title: 'Your Tool Belt', duration: '12 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-1', order: 1, title: 'Chaining Prompts', duration: '15 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-2', order: 2, title: 'Templates & Playbooks', duration: '12 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-3', order: 3, title: 'When to Automate', duration: '12 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-4', order: 4, title: 'The Feedback Loop', duration: '10 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-5', order: 5, title: 'Stack Audit', duration: '20 min', tracks: ['A', 'B', 'C'] },
    ],
  },
  {
    id: 'module-4',
    order: 4,
    title: 'The Review',
    subtitle: 'Operating at Full Capacity',
    gated: true,
    passingScore: 80,
    lessons: [
      { id: 'lesson-0', order: 0, title: 'Field Test', duration: '30 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-1', order: 1, title: 'Operator Patterns', duration: '12 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-2', order: 2, title: 'Edge Cases', duration: '10 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-3', order: 3, title: 'Teaching Others', duration: '12 min', tracks: ['A', 'B', 'C'] },
      { id: 'lesson-4', order: 4, title: 'Certification', duration: '45 min', tracks: ['A', 'B', 'C'] },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
export function getModule(moduleId) {
  return MODULES.find((m) => m.id === moduleId) || null;
}

export function getLesson(moduleId, lessonId) {
  const mod = getModule(moduleId);
  return mod ? mod.lessons.find((l) => l.id === lessonId) || null : null;
}

export function getNextLesson(moduleId, lessonId) {
  const mod = getModule(moduleId);
  if (!mod) return null;
  const idx = mod.lessons.findIndex((l) => l.id === lessonId);
  if (idx < mod.lessons.length - 1) {
    return { moduleId, lessonId: mod.lessons[idx + 1].id };
  }
  // Move to next module
  const modIdx = MODULES.findIndex((m) => m.id === moduleId);
  if (modIdx < MODULES.length - 1) {
    const nextMod = MODULES[modIdx + 1];
    return { moduleId: nextMod.id, lessonId: nextMod.lessons[0].id };
  }
  return null; // Course complete
}

export function getPrevLesson(moduleId, lessonId) {
  const mod = getModule(moduleId);
  if (!mod) return null;
  const idx = mod.lessons.findIndex((l) => l.id === lessonId);
  if (idx > 0) {
    return { moduleId, lessonId: mod.lessons[idx - 1].id };
  }
  // Move to previous module's last lesson
  const modIdx = MODULES.findIndex((m) => m.id === moduleId);
  if (modIdx > 0) {
    const prevMod = MODULES[modIdx - 1];
    return { moduleId: prevMod.id, lessonId: prevMod.lessons[prevMod.lessons.length - 1].id };
  }
  return null; // Already at start
}

export function scoreToTrack(scores) {
  const sorted = Object.entries(scores).sort(([, a], [, b]) => b - a);
  return sorted[0][0]; // Track with highest score
}
